import React, { useState } from 'react';
import { Box, Chip, Typography, Skeleton, Button } from '@mui/material';
import { SectionCard } from '../../../../components/ui';
import { brandHome } from '../../brandHomeTokens';
import { pickStoreLayout } from '../../layoutThreshold';
import type { StoreViewModel } from '../../storeViewModel';
import { FewStoreLayout } from './FewStoreLayout';
import { ManyStoreLayout } from './ManyStoreLayout';

interface StoresCardProps {
  stores: StoreViewModel[];
  loading?: boolean;
}

type StoreFilter = 'All stores' | 'Only with issues';

export const StoresCard: React.FC<StoresCardProps> = ({ stores, loading }) => {
  const [filter, setFilter] = useState<StoreFilter>('All stores');
  const visible = filter === 'Only with issues' ? stores.filter((s) => s.hasIssues) : stores;
  const layout = pickStoreLayout(stores.length);

  return (
    <SectionCard
      dense
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>Stores carrying your brand</Typography>
          <Chip label={stores.length} size="small" sx={{ bgcolor: brandHome.color.chipFill, color: brandHome.color.bodyAlt, fontWeight: 700 }} />
        </Box>
      }
      actions={
        <Box sx={{ display: 'flex', gap: '5px' }}>
          {(['All stores', 'Only with issues'] as StoreFilter[]).map((label) => (
            <Button
              key={label}
              size="small"
              onClick={() => setFilter(label)}
              variant={filter === label ? 'contained' : 'outlined'}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                bgcolor: filter === label ? brandHome.color.ink : brandHome.color.surface,
                borderColor: brandHome.color.border,
                color: filter === label ? '#fff' : brandHome.color.bodyAlt,
                '&:hover': { bgcolor: filter === label ? brandHome.color.ink : brandHome.color.surface },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      }
    >
      {loading ? (
        <Box sx={{ p: '16px 20px' }}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rectangular" height={110} sx={{ mb: 1.5, borderRadius: 1 }} />
          ))}
        </Box>
      ) : visible.length === 0 ? (
        <Box sx={{ p: '24px 20px', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: brandHome.color.muted }}>
            No stores have selected your brand yet.
          </Typography>
        </Box>
      ) : layout === 'many' ? (
        <ManyStoreLayout stores={visible} />
      ) : (
        <FewStoreLayout stores={visible} />
      )}
    </SectionCard>
  );
};
